import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '../auth/AuthProvider'
import { errorMessage } from '../api/client'
import { useMailAccountActions, useProviderActions } from '../api/hooks'
import { Field } from '../components/ui'

type Step = 'admin' | 'mail' | 'provider' | 'done'

const STEPS: { key: Step; label: string }[] = [
  { key: 'admin', label: 'Administrator' },
  { key: 'mail', label: 'Mailkonto' },
  { key: 'provider', label: 'KI-Provider' },
  { key: 'done', label: 'Fertig' },
]

/**
 * First-run wizard. Every step after the admin is skippable - the system has to be
 * usable before a single model or mailbox exists.
 */
export function SetupPage({ onFinished }: { onFinished: () => void }) {
  const { setup } = useAuth()
  const navigate = useNavigate()
  const mailAccounts = useMailAccountActions()
  const providers = useProviderActions()

  const [step, setStep] = useState<Step>('admin')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [admin, setAdmin] = useState({ email: '', password: '', repeat: '', name: '' })
  const [mail, setMail] = useState({
    name: 'Privat',
    kind: 'gmail',
    username: '',
    secret: '',
    host: '',
    port: '',
    privacy_level: 'local_only',
  })
  const [provider, setProvider] = useState({
    name: 'Ollama',
    kind: 'ollama',
    base_url: 'http://ollama:11434/v1',
    default_model: '',
    tier: 'batch',
  })

  async function submitAdmin(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    if (admin.password !== admin.repeat) {
      setError('Die Passwörter stimmen nicht überein.')
      return
    }
    setBusy(true)
    try {
      await setup(admin.email, admin.password, admin.name)
      setStep('mail')
    } catch (caught) {
      setError(errorMessage(caught, 'Die Einrichtung ist fehlgeschlagen.'))
    } finally {
      setBusy(false)
    }
  }

  async function submitMail(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await mailAccounts.create.mutateAsync({
        name: mail.name,
        kind: mail.kind,
        username: mail.username,
        secret: mail.secret,
        host: mail.host || null,
        port: mail.port ? Number(mail.port) : null,
        privacy_level: mail.privacy_level,
        auth_type: 'password',
        enabled: true,
      })
      setStep('provider')
    } catch (caught) {
      setError(errorMessage(caught, 'Das Konto konnte nicht angelegt werden.'))
    } finally {
      setBusy(false)
    }
  }

  async function submitProvider(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await providers.create.mutateAsync({
        name: provider.name,
        kind: provider.kind,
        base_url: provider.base_url,
        default_model: provider.default_model || null,
        model_map: provider.default_model ? { [provider.default_model]: provider.tier } : { '*': provider.tier },
        windows:
          provider.tier === 'batch'
            ? [{ days: [0, 1, 2, 3, 4], from: '07:00', to: '16:00', mode: 'blocked' }]
            : [],
        enabled: true,
        privacy_class: 'local_only',
      })
      setStep('done')
    } catch (caught) {
      setError(errorMessage(caught, 'Der Provider konnte nicht angelegt werden.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-10">
      <h1 className="font-display text-3xl">Backoffice einrichten</h1>
      <p className="mt-2 max-w-prose text-sm text-paper-600 dark:text-paper-300">
        Drei Schritte. Mailkonto und KI-Provider lassen sich auch später in den
        Einstellungen ergänzen.
      </p>

      <ol className="my-6 flex flex-wrap gap-2 text-sm" aria-label="Fortschritt">
        {STEPS.map((entry, index) => {
          const current = entry.key === step
          const done = STEPS.findIndex((item) => item.key === step) > index
          return (
            <li
              key={entry.key}
              aria-current={current ? 'step' : undefined}
              className={`chip ${current ? 'border-ink-500 text-ink-700 dark:text-ink-200' : ''} ${done ? 'opacity-60' : ''}`}
            >
              {index + 1}. {entry.label}
            </li>
          )
        })}
      </ol>

      {error ? (
        <p role="alert" className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950/50 dark:text-red-100">
          {error}
        </p>
      ) : null}

      {step === 'admin' ? (
        <form onSubmit={submitAdmin} className="card" noValidate>
          <h2 className="mb-4 text-lg">Administrator anlegen</h2>
          <Field label="E-Mail">
            {(props) => (
              <input
                {...props}
                className="input"
                type="email"
                autoComplete="username"
                required
                value={admin.email}
                onChange={(event) => setAdmin({ ...admin, email: event.target.value })}
              />
            )}
          </Field>
          <Field label="Name">
            {(props) => (
              <input
                {...props}
                className="input"
                value={admin.name}
                onChange={(event) => setAdmin({ ...admin, name: event.target.value })}
              />
            )}
          </Field>
          <Field label="Passwort" hint="Mindestens 12 Zeichen.">
            {(props) => (
              <input
                {...props}
                className="input"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={admin.password}
                onChange={(event) => setAdmin({ ...admin, password: event.target.value })}
              />
            )}
          </Field>
          <Field label="Passwort wiederholen">
            {(props) => (
              <input
                {...props}
                className="input"
                type="password"
                autoComplete="new-password"
                required
                value={admin.repeat}
                onChange={(event) => setAdmin({ ...admin, repeat: event.target.value })}
              />
            )}
          </Field>
          <button type="submit" className="btn-primary w-full" disabled={busy}>
            {busy ? 'Wird angelegt…' : 'Weiter'}
          </button>
        </form>
      ) : null}

      {step === 'mail' ? (
        <form onSubmit={submitMail} className="card" noValidate>
          <h2 className="mb-4 text-lg">Erstes Mailkonto</h2>
          <p className="mb-4 text-sm text-paper-500">
            Backoffice liest Postfächer ausschliesslich lesend. Es wird nichts markiert,
            verschoben oder gelöscht.
          </p>
          <Field label="Bezeichnung">
            {(props) => (
              <input
                {...props}
                className="input"
                value={mail.name}
                onChange={(event) => setMail({ ...mail, name: event.target.value })}
              />
            )}
          </Field>
          <Field label="Typ">
            {(props) => (
              <select
                {...props}
                className="input"
                value={mail.kind}
                onChange={(event) => setMail({ ...mail, kind: event.target.value })}
              >
                <option value="gmail">Gmail</option>
                <option value="icloud">iCloud</option>
                <option value="imap">IMAP</option>
              </select>
            )}
          </Field>
          {mail.kind === 'imap' ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Host">
                {(props) => (
                  <input
                    {...props}
                    className="input"
                    value={mail.host}
                    onChange={(event) => setMail({ ...mail, host: event.target.value })}
                  />
                )}
              </Field>
              <Field label="Port">
                {(props) => (
                  <input
                    {...props}
                    className="input"
                    inputMode="numeric"
                    value={mail.port}
                    onChange={(event) => setMail({ ...mail, port: event.target.value })}
                  />
                )}
              </Field>
            </div>
          ) : null}
          <Field label="Benutzername">
            {(props) => (
              <input
                {...props}
                className="input"
                autoComplete="off"
                required
                value={mail.username}
                onChange={(event) => setMail({ ...mail, username: event.target.value })}
              />
            )}
          </Field>
          <Field label="App-Passwort" hint="Wird mit dem Master-Key verschlüsselt gespeichert.">
            {(props) => (
              <input
                {...props}
                className="input"
                type="password"
                autoComplete="off"
                required
                value={mail.secret}
                onChange={(event) => setMail({ ...mail, secret: event.target.value })}
              />
            )}
          </Field>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary" disabled={busy}>
              Konto anlegen
            </button>
            <button type="button" className="btn-ghost" onClick={() => setStep('provider')}>
              Überspringen
            </button>
          </div>
        </form>
      ) : null}

      {step === 'provider' ? (
        <form onSubmit={submitProvider} className="card" noValidate>
          <h2 className="mb-4 text-lg">KI-Provider</h2>
          <p className="mb-4 text-sm text-paper-500">
            Ohne Provider funktioniert alles weiter: Ingest, Volltext- und Vektorsuche und
            Regeln. Die KI-Jobs warten sichtbar in der Warteschlange.
          </p>
          <Field label="Bezeichnung">
            {(props) => (
              <input
                {...props}
                className="input"
                value={provider.name}
                onChange={(event) => setProvider({ ...provider, name: event.target.value })}
              />
            )}
          </Field>
          <Field label="Basis-URL" hint="OpenAI-kompatibler Endpunkt, z. B. http://ollama:11434/v1">
            {(props) => (
              <input
                {...props}
                className="input"
                value={provider.base_url}
                onChange={(event) => setProvider({ ...provider, base_url: event.target.value })}
              />
            )}
          </Field>
          <Field label="Modell" hint="Optional - kann in den Einstellungen aus der Liste gewählt werden.">
            {(props) => (
              <input
                {...props}
                className="input"
                value={provider.default_model}
                onChange={(event) => setProvider({ ...provider, default_model: event.target.value })}
              />
            )}
          </Field>
          <Field label="Tier">
            {(props) => (
              <select
                {...props}
                className="input"
                value={provider.tier}
                onChange={(event) => setProvider({ ...provider, tier: event.target.value })}
              >
                <option value="batch">Batch — werktags 07–16 Uhr gesperrt</option>
                <option value="heavy">Schwer — nur schwierige Fälle</option>
                <option value="always">Immer</option>
              </select>
            )}
          </Field>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary" disabled={busy}>
              Provider anlegen
            </button>
            <button type="button" className="btn-ghost" onClick={() => setStep('done')}>
              Überspringen
            </button>
          </div>
        </form>
      ) : null}

      {step === 'done' ? (
        <div className="card">
          <h2 className="mb-2 text-lg">Fertig</h2>
          <p className="text-sm text-paper-600 dark:text-paper-300">
            Backoffice ist einsatzbereit. Der erste Import läuft im Hintergrund als Backlog-Job,
            neueste Nachrichten zuerst.
          </p>
          <button
            type="button"
            className="btn-primary mt-4"
            onClick={() => {
              onFinished()
              void navigate({ to: '/' })
            }}
          >
            Zur Übersicht
          </button>
        </div>
      ) : null}
    </div>
  )
}
